import { useLoginOrConnectWallet } from '@/app/auth/artblocks-auth-provider'
import { useAuthenticatedWalletClient } from '@/hooks/use-authenticated-wallet-client'
import { useModalStatus } from '@privy-io/react-auth'
import { useSelector, useStore } from '@xstate/store/react'
import { useEffect } from 'react'
import { WalletClient } from 'viem'

/**
 * Manages the state and interactions for secondary market owner actions (List for Sale, Accept Bid).
 *
 * This hook orchestrates the opening of modals for listing a token or accepting a bid.
 * It is designed to be used in components that are only displayed when the current user
 * is known to be the owner of the token. Therefore, it assumes the user is already authenticated.
 *
 * The primary check performed by this hook is for wallet connection status.
 * If the user clicks "List for Sale" or "Accept Bid" while their wallet is disconnected,
 * it will trigger the wallet connection flow (`loginOrConnectWallet`).
 * Upon successful wallet connection, it seamlessly opens the originally intended modal.
 *
 * @returns An object containing boolean states for modal visibility (`listModalOpen`, `acceptBidModalOpen`)
 *          and functions to trigger opening/closing the modals (`openListingModal`, `openAcceptBidModal`,
 *          `closeListingModal`, `closeAcceptBidModal`).
 */
export function useSecondaryOwnerModalState(): {
  listModalOpen: boolean
  acceptBidModalOpen: boolean
  openListingModal: () => void
  openAcceptBidModal: () => void
  closeListingModal: () => void
  closeAcceptBidModal: () => void
} {
  const walletClient = useAuthenticatedWalletClient()
  const { loginOrConnectWallet } = useLoginOrConnectWallet()
  const { isOpen: isPrivyModalOpen } = useModalStatus()

  const store = useStore({
    context: {
      listModalOpen: false,
      acceptBidModalOpen: false,
      connectingBeforeOpeningListModal: false,
      connectingBeforeOpeningAcceptBidModal: false,
      walletClient
    },
    on: {
      openListingModal: (context) => {
        if (!context.walletClient) {
          loginOrConnectWallet()
          return { ...context, connectingBeforeOpeningListModal: true }
        }

        return { ...context, listModalOpen: true }
      },
      openAcceptBidModal: (context) => {
        if (!context.walletClient) {
          loginOrConnectWallet()
          return { ...context, connectingBeforeOpeningAcceptBidModal: true }
        }

        return { ...context, acceptBidModalOpen: true }
      },
      closeListingModal: (context) => {
        return { ...context, listModalOpen: false }
      },
      closeAcceptBidModal: (context) => {
        return { ...context, acceptBidModalOpen: false }
      },
      walletClientUpdate: (context, event: { walletClient: WalletClient | undefined }) => {
        if (!event.walletClient) {
          return { ...context, walletClient: undefined, listModalOpen: false, acceptBidModalOpen: false }
        }

        return {
          ...context,
          connectingBeforeOpeningListModal: false,
          connectingBeforeOpeningAcceptBidModal: false,
          walletClient: event.walletClient,
          listModalOpen: context.connectingBeforeOpeningListModal,
          acceptBidModalOpen: context.connectingBeforeOpeningAcceptBidModal
        }
      },
      connectModalClose: (context) => {
        if (!context.walletClient) {
          return { ...context, connectingBeforeOpeningListModal: false, connectingBeforeOpeningAcceptBidModal: false }
        }

        return { ...context }
      }
    }
  })

  const { listModalOpen, acceptBidModalOpen } = useSelector(store, (snapshot) => {
    return snapshot.context
  })

  useEffect(() => {
    store.trigger.walletClientUpdate({ walletClient })
  }, [walletClient, store])

  useEffect(() => {
    if (!isPrivyModalOpen) {
      store.trigger.connectModalClose()
    }
  }, [isPrivyModalOpen, store])

  return {
    listModalOpen,
    acceptBidModalOpen,
    openListingModal: () => store.trigger.openListingModal(),
    openAcceptBidModal: () => store.trigger.openAcceptBidModal(),
    closeListingModal: () => store.trigger.closeListingModal(),
    closeAcceptBidModal: () => store.trigger.closeAcceptBidModal()
  }
}
